import React, { useEffect, useState, useRef } from "react";
import useAuth from '../../../hooks/useAuth';
import { Validation } from '../../../utils/validation';
import bonusStructureImg from '../../../assets/images/bonus-structure-img.jpg';
import ShareBonusModal from '../../../components/Modals/ShareBonusModal';
import ShareMessageModal from '../../../components/Modals/ShareMessageModal';
import BestImprovementAwardPDF from "../pdf/BestImprovementAwardPDF";
import Loader from "../../../components/Loader/Loader";
import html2pdf from "html2pdf.js";
import moment from 'moment';
import BonusService from "../../../services/BonusService";

const BestImprovementAwardProgramTab = ({ active }) => {
    const {user} = useAuth();
    const [errors, setErrors] = useState({});
    const [showShareModal, setShowShareModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showLoader, setShowLoader] = useState(false);
    const [showShareMessageModal, setShowShareMessageModal] = useState(false);
    const pdfRef = useRef();

    const initialFormState = {
        portal_user_id: user.id, // Will be set in useEffect or on mount
        full_name: '',
        department: '',
        job_title: '',
        date_submission: '',
        improvement_idea: '',
        department_idea: '',
        improvement_details: '',
        improvement_implemented: '',
        tools: '',
        benefits: '',
        additional_comments: '',
        year: moment().year()
    };
    
    
    const [formData, setFormData] = useState(initialFormState);

    const resetForm = () => {
        setFormData({
            ...initialFormState,
            portal_user_id: user.id // keep the user ID
        });
        setErrors({}); // clear any validation messages
    };

    const bonusService = new BonusService();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    }

    const validateForm = (e) => {
        e.preventDefault();
        const errors = Validation(formData,['full_name','department','job_title','date_submission','improvement_idea','department_idea','improvement_details','improvement_implemented','tools','benefits']);
        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            return;
        }
        setFormData((prevData) => ({
            ...prevData,
            email: ''
        }));
        setShowShareModal(true);
    }

    const handleFinalSubmit = () => {
        setErrors({});
        formData.date_submission = formData.date_submission !== '' ? moment(formData.date_submission).format('YYYY-MM-DD') : '';
        
        setShowShareModal(false);
        setShowShareMessageModal(true);
        
        bonusService.submitImprovementIdea(formData)
            .then(response => {
                if (response.data.status === 'success') {
                    setErrorMessage('');
                    setSuccessMessage(response.data.message || 'Your improvement idea has been submitted successfully!');
                    resetForm();
                } else {
                    setSuccessMessage('');
                    setErrorMessage(response.data.message || 'Failed to submit your improvement idea. Please try again.');
                }
            })
            .catch(error => {
                console.error('API error:', error);
                setSuccessMessage('');
                setErrorMessage('An error occurred while submitting your improvement idea. Please try again later.');
            })
            .finally(() => {
                // Auto-hide the success/error message after 3 seconds
                setTimeout(() => {
                    setShowShareMessageModal(false);
                    setSuccessMessage('');
                    setErrorMessage('');
                }, 3000);
            });
    }

    const handleDownload = (e) => {
        e.preventDefault();

        const errors = Validation(formData, [
            'full_name',
            'department',
            'job_title',
            'date_submission',
            'improvement_idea',
            'department_idea',
            'improvement_details',
            'improvement_implemented',
            'tools',
            'benefits'
        ]);

        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            return;
        }
        setShowLoader(true);

        const element = pdfRef.current;

        html2pdf()
            .set({
                margin: 10,
                filename: `Best_Improvement_Award_${moment().format('YYYYMMDDHHMMSS')}.pdf`,
                html2canvas: { scale: 2 },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
            })
            .from(element)
            .save()
            .then(() => {
                setShowLoader(false);
                resetForm(); // <--- RESET HERE after PDF is generated
            });
    }

    return (
        <div className={`container tab-pane fade ${active ? 'show active' : ''} py-3 px-0`} id="best-improvement-award-program" role="tabpanel">
            <div className="row">
                <div className="col-md-10 offset-md-1">
                    <img src={bonusStructureImg} className="img-fluid d-block mx-auto w-100" alt="Bonus Structure Image"/>	
                    <h4 className="text-center my-3">Best Improvement Award Program</h4>
                </div>	
            </div>	
            <div className="row">
                <div className="col-md-12">
                    <ul>
                        <li>Best Improvement Award: An internal recognition program that rewards the most impactful improvement ideas submitted by employees.</li>
                        <li>Improvement idea: A practical, implementable proposal that enhances operations, reduces costs, improves efficiency, quality, safety, customer experience, or internal processes.</li>
                        <li>Official Submission Form: The approved Techtop form required for all Best Improvement Award entries.</li>
                    </ul>
                    <h4 className="text-center my-3">The Best Improvement Award program will continue as-is.</h4>
                    <p className="mb-0"><strong>Please remember:</strong></p>
                    <p>Use the official submission forms for all improvement ideas.</p>
                    <p>Team members who submitted ideas previously that did not place in the top two are encouraged to resubmit those ideas and/or submit new ones.</p>
                    <h5 className="m-0 text-center">Awards</h5>
                    <h5 className="text-center m-0">1<sup>st</sup> Place- $3,000 </h5>
                    <h5 className="text-center">2<sup>nd</sup> Place- $1,500</h5>
                </div>	
            </div>
            <div className="row">
                <div className="col-md-12">
                    <p>Thank you for your contribution to improving our company! Please complete the form below with details of your improvement idea. Submissions must be in writing to be considered for the Best Improvement Awards at the end of the year.</p>
                    <form onSubmit={validateForm}>
                        <div className="row">
                            <div className="col-md-12">
                                <h4>Employee Information</h4>
                            </div>
                            <div className="row employee-information-div g-1">
                                <div className="col-md-5">
                                    <ul>
                                        <li>
                                            <div className="mb-3 row g-1">
                                                <label htmlFor="full_name" className="form-label col-md-4">Full Name</label>
                                                <div className="col-md-8">
                                                    <input type="text" className="form-control" id="full_name" name="full_name" onChange={handleInputChange} value={formData.full_name}/>
                                                    {errors.full_name && <div className="text-danger">{errors.full_name}</div>}
                                                </div>	
                                            </div>
                                        </li>
                                        <li>
                                            <div className="mb-3 row g-1">
                                                <label htmlFor="department" className="form-label col-md-4">Department</label>
                                                <div className="col-md-8">
                                                    <input type="text" className="form-control" id="department" name="department" onChange={handleInputChange} value={formData.department}/> 
                                                    {errors.department && <div className="text-danger">{errors.department}</div>}
                                                </div>	
                                            </div>
                                        </li>
                                        <li>
                                            <div className="mb-3 row g-1">
                                                <label htmlFor="job_title" className="form-label col-md-4">Job Title</label>
                                                <div className="col-md-8">
                                                    <input type="text" className="form-control" id="job_title" name="job_title" onChange={handleInputChange} value={formData.job_title}/>
                                                    {errors.job_title && <div className="text-danger">{errors.job_title}</div>}
                                                </div>	
                                            </div>
                                        </li>
                                        <li>
                                            <div className="mb-3 row g-1">
                                                <label htmlFor="date_submission" className="form-label col-md-4">Date of Submission</label>
                                                <div className="col-md-8">
                                                    <input type="date" className="form-control" id="date_submission" name="date_submission" onChange={handleInputChange} value={formData.date_submission}/> 
                                                    {errors.date_submission && <div className="text-danger">{errors.date_submission}</div>}
                                                </div>	
                                            </div>
                                        </li>	
                                    </ul>
                                </div>
                            </div>	
                        </div>	
                        <div className="row">	
                            <div className="col-md-12">
                                <h4>Improvement Idea</h4>
                            </div>
                        </div>	
                        <div className="row improvement-idea-div g-1">
                            <div className="col-md-8"> 
                                <ul>
                                    <li>
                                        <div className="mb-3 row g-1">
                                            <label htmlFor="improvement_idea" className="form-label col-md-5">Title of Improvement Idea</label>
                                            <div className="col-md-7">
                                                <input type="text" className="form-control" id="improvement_idea" name="improvement_idea" onChange={handleInputChange} value={formData.improvement_idea}/> 
                                                {errors.improvement_idea && <div className="text-danger">{errors.improvement_idea}</div>}
                                            </div>	
                                        </div>
                                    </li>
                                </ul>	
                            </div>	
                        </div>
                        <div className="row improvement-idea-div g-1">	 
                            <div className="col-md-8">
                                <ul>
                                    <li>
                                        <div className="mb-3 row g-1">
                                            <label htmlFor="department_idea" className="form-label col-md-5">What department does this idea pertain to?</label>
                                            <div className="col-md-7">
                                                <input type="text" className="form-control" id="department_idea" name="department_idea" onChange={handleInputChange} value={formData.department_idea}/> 
                                                {errors.department_idea && <div className="text-danger">{errors.department_idea}</div>}
                                            </div>	
                                        </div>
                                    </li>
                                </ul>			
                            </div>
                        </div>
                        <div className="row">	
                            <div className="col-md-12">														
                                <div className="mb-3">
                                    <ul>
                                        <li>
                                            <label htmlFor="improvement_details" className="form-label">Describe your improvement idea in detail</label>
                                        </li>
                                    </ul>	
                                    <textarea className="form-control" id="improvement_details" rows="3" name="improvement_details" onChange={handleInputChange} value={formData.improvement_details}></textarea>
                                    {errors.improvement_details && <div className="text-danger">{errors.improvement_details}</div>}
                                </div>																
                            </div>
                            <div className="col-md-12">
                                <h4>Implementation Plan</h4>															
                                <div className="mb-3">
                                    <ul>
                                        <li>
                                            <label htmlFor="improvement_implemented" className="form-label">How will this improvement be implemented?</label>
                                        </li>
                                    </ul>	
                                    <textarea className="form-control" id="improvement_implemented" rows="3" name="improvement_implemented" onChange={handleInputChange} value={formData.improvement_implemented}></textarea>
                                    {errors.improvement_implemented && <div className="text-danger">{errors.improvement_implemented}</div>}
                                </div>																
                            </div>
                            <div className="col-md-12">														
                                <div className="mb-3">
                                    <ul>
                                        <li>
                                            <label htmlFor="tools" className="form-label">What resources, tools, or support are needed for success?</label>
                                        </li>
                                    </ul>		
                                    <textarea className="form-control" id="tools" rows="2" name="tools" onChange={handleInputChange} value={formData.tools}></textarea>
                                    {errors.tools && <div className="text-danger">{errors.tools}</div>}
                                </div>
                                    
                            </div>
                            <div className="col-md-12">														
                                <div className="mb-3">
                                    <ul>
                                        <li>
                                            <label htmlFor="benefits" className="form-label">What benefits will this improvement bring?</label>
                                        </li>
                                    </ul>	
                                    <textarea className="form-control" id="benefits" rows="2" name="benefits" onChange={handleInputChange} value={formData.benefits}></textarea>
                                    {errors.benefits && <div className="text-danger">{errors.benefits}</div>}
                                </div>																
                            </div>
                            <div className="col-md-12">
                                <div className="mb-3">
                                    <label htmlFor="additional_comments" className="form-label">Additional Comments</label>
                                    <textarea className="form-control" id="additional_comments" rows="2" name="additional_comments" onChange={handleInputChange} value={formData.additional_comments}></textarea>
                                </div>
                            </div>
                            <div className="col-md-12 my-3 text-end">
                                <button type="button" className="btn download-btn" onClick={handleDownload}>Download</button>
                                <button type="submit" className="btn submit-btn">Submit</button>
                            </div>
                        </div>												
                    </form> 
                </div>
                <div className="col-md-12">
                    {/* <p><strong>Employee Signature:</strong> _____________________</p>
                    <p><strong>Date:</strong> __________</p>  */}
                    <p className="mb-0">Please submit this completed form directly to Li Zheng for consideration.</p> 
                    <p className="mb-0">Submitting this form enters you into candidacy for the “Best Improvement Award”.</p> 
                    <p>This award will recognize employees whose improvement ideas significantly enhance our
                    operations, management, or sales.</p> 
                    <p>* Two awards will be presented at the end of the year: $3,000 for first place and $1,500 for
                    the runner-up.</p> 
                    <p>* To ensure consideration, please submit all suggestions for improvement in writing to me.</p> 
                    <p>We appreciate your innovative ideas and dedication to making our company better!</p>
                </div>	
            </div>
            <ShareBonusModal 
                isShow={showShareModal} 
                setFormData={setFormData} 
                formData={formData} 
                onFinalSubmit={handleFinalSubmit} 
                onClose={() => setShowShareModal(false)} 
            />

            <ShareMessageModal 
                isShow={showShareMessageModal} 
                onClose={() => setShowShareMessageModal(false)} 
                successMessage={successMessage} 
                errorMessage={errorMessage}
            />
            <Loader isLoading={showLoader} message="Processing..."/>
            <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
                <div ref={pdfRef}>
                    <BestImprovementAwardPDF formData={formData} />
                </div>
            </div>
        </div>
    )
}

export default BestImprovementAwardProgramTab;